import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { App as AntdApp, ConfigProvider, Spin } from 'antd';
import viVN from 'antd/locale/vi_VN';
import enUS from 'antd/locale/en_US';

// Chuẩn chính tả 'huỷ' (khớp 'hoá/khoá' ở toàn backoffice): antd vi_VN mặc định dùng 'Hủy' cho nút Huỷ của Modal/Popconfirm.
const viLocale = {
  ...viVN,
  Modal: { ...viVN.Modal, cancelText: 'Huỷ' },
  Popconfirm: { ...viVN.Popconfirm, cancelText: 'Huỷ' },
};
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { queryClient } from './services/queryClient';
import { AppRoutes } from './routes/AppRoutes';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { useLanguageStore } from './store/languageStore';

const PageLoading: React.FC = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#F5F7FA'
  }}>
    <Spin size="large" tip="Loading page..." />
  </div>
);

const App: React.FC = () => {
  const { initializeLanguage, language } = useLanguageStore();

  useEffect(() => {
    // Initialize language store with detected language on app load
    initializeLanguage();
  }, [initializeLanguage]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider
          locale={language === 'vi' ? viLocale : enUS}
          theme={{
            token: {
              colorPrimary: '#3b82f6',
              colorSuccess: '#16a34a',
              colorWarning: '#d97706',
              colorError: '#dc2626',
              colorInfo: '#0284c7',
              colorBgBase: '#F5F7FA',
              colorBgContainer: '#ffffff',
              colorBorder: '#EDEDED',
              borderRadius: 6,
            },
            components: {
              // Breadcrumb đủ tương phản trên nền sáng (mặc định antd link #1890FF ~2,8:1)
              Breadcrumb: {
                linkColor: '#0B6E78',
                linkHoverColor: '#0A5F69',
                itemColor: '#4F6360',
                lastItemColor: '#12302F',
                separatorColor: '#6B7F7B',
              },
              Menu: {
                itemPaddingInline: 16,
                subMenuItemBg: 'transparent',
                darkSubMenuItemBg: 'transparent',
              },
            },
          }}
        >
          {/* antd's <App> cấp context thật cho App.useApp() (message/notification/modal) — thiếu
              wrapper này, App.useApp() trả về context mặc định { message: {}, notification: {}, modal: {} }
              (xem antd/es/app/context.js), nên message.success()/message.error()/modal.confirm() ném
              TypeError ở BẤT KỲ đâu dùng App.useApp() (19 file trong cms-backoffice-module) — bug thật
              đã gặp: TagCreateEditPage tạo tag thành công (201) nhưng message.success() throw ngay
              trước navigate() trong cùng callback, khiến navigate() không bao giờ chạy tới, form đứng
              yên không rõ lý do. */}
          <AntdApp>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <AuthProvider>
                <TenantProvider>
                  <Suspense fallback={<PageLoading />}>
                    <AppRoutes />
                  </Suspense>
                </TenantProvider>
              </AuthProvider>
            </Router>
          </AntdApp>
        </ConfigProvider>
        
        {/* React Query DevTools in development */}
        {import.meta.env.MODE === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;